const PCM_TARGET_SAMPLE_RATE = 24_000;

/**
 * Keeps resampling phase across Web Audio callbacks so device rates such as
 * 44.1 kHz do not accumulate timestamp drift when converted to Dicta's 24 kHz
 * analysis stream.
 */
export class StreamingLinearResampler {
  private readonly step: number;
  private position = 0;
  private previousSample: number | null = null;

  constructor(
    readonly inputSampleRate: number,
    readonly outputSampleRate = PCM_TARGET_SAMPLE_RATE
  ) {
    if (
      !Number.isFinite(inputSampleRate) ||
      !Number.isFinite(outputSampleRate) ||
      inputSampleRate <= 0 ||
      outputSampleRate <= 0
    ) {
      throw new Error("Audio sample rates must be positive finite numbers.");
    }
    this.step = inputSampleRate / outputSampleRate;
  }

  process(input: Float32Array): Float32Array {
    if (input.length === 0) return new Float32Array();
    if (this.inputSampleRate === this.outputSampleRate) return input.slice();

    const combined = new Float32Array(
      input.length + (this.previousSample === null ? 0 : 1)
    );
    let offset = 0;
    if (this.previousSample !== null) {
      combined[0] = this.previousSample;
      offset = 1;
    }
    combined.set(input, offset);

    const output: number[] = [];
    while (this.position < combined.length - 1) {
      const leftIndex = Math.floor(this.position);
      const fraction = this.position - leftIndex;
      const left = combined[leftIndex] ?? 0;
      const right = combined[leftIndex + 1] ?? left;
      output.push(left + (right - left) * fraction);
      this.position += this.step;
    }

    this.position -= combined.length - 1;
    this.previousSample = combined[combined.length - 1] ?? null;
    return Float32Array.from(output);
  }
}

export function preferredRecorderMimeType(): string | null {
  if (typeof MediaRecorder === "undefined") return null;
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/webm"
  ];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) ?? "";
}

export function captureSupportFailure(): string | null {
  if (!navigator.mediaDevices?.getUserMedia) {
    return "This browser cannot request microphone access. Use a current Safari, Chrome or Edge browser.";
  }
  if (typeof AudioContext === "undefined" || typeof AudioWorkletNode === "undefined") {
    return "This browser cannot provide evidence-safe audio capture. Use the current or previous Safari, Chrome or Edge release.";
  }
  if (preferredRecorderMimeType() === null) {
    return "This browser cannot preserve a source recording. Update Safari, Chrome or Edge before creating a transcript.";
  }
  return null;
}

export function captureStartErrorMessage(cause: unknown): string {
  const candidate =
    cause && typeof cause === "object"
      ? (cause as { name?: unknown; message?: unknown })
      : null;
  const name = typeof candidate?.name === "string" ? candidate.name : "";
  const message =
    typeof candidate?.message === "string"
      ? candidate.message
      : cause instanceof Error
        ? cause.message
        : "";

  if (
    name === "NotAllowedError" ||
    name === "PermissionDeniedError" ||
    name === "SecurityError" ||
    /permission denied|not allowed/i.test(message)
  ) {
    return "Microphone access is blocked. Allow Microphone for dicta.io in browser site settings and for Safari or Chrome in phone Settings, then reload. Recording-authority confirmation is separate from device permission.";
  }
  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return "No microphone was found. Connect or enable a microphone, then try again.";
  }
  if (name === "NotReadableError" || name === "TrackStartError") {
    return "Microphone is unavailable. Close other apps using it, check phone or computer microphone access, then try again.";
  }
  if (name === "AbortError") {
    return "Microphone startup was interrupted. Keep Dicta open, then try again.";
  }
  return message || "Microphone access failed. Allow access, then start recording again.";
}

/**
 * MediaRecorder.mimeType and the emitted Blob.type are not guaranteed to use
 * the same parameter string (notably on Safari). Lock the source stream to the
 * first emitted chunk's declared type so every append and the final commit use
 * one canonical value.
 */
export function canonicalRecorderMimeType(
  establishedMimeType: string,
  blobMimeType: string,
  recorderMimeType: string
): string {
  return (
    establishedMimeType.trim() ||
    blobMimeType.trim() ||
    recorderMimeType.trim() ||
    "audio/webm"
  );
}
